import React, { useEffect, useState } from 'react';
import { getAgents } from '../../api/client';

interface Agent {
  name: string;
  slug: string;
  ready: boolean;
  has_profile: boolean;
}

interface AgentSelectorProps {
  accountId: string | null;
  workspaceId: string | null;
  workspaceName: string | null;
  onSelect: (slug: string, name: string, hasProfile: boolean) => void;
  selectedSlug: string | null;
}

const AgentSelector: React.FC<AgentSelectorProps> = ({ accountId, workspaceId, workspaceName, onSelect, selectedSlug }) => {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch agents whenever the workspace context changes. Auto-select is
  // handled by the separate effect below so we don't rely on the closure
  // seeing a fresh selectedSlug (which is stale when the parent's
  // workspace-change reset hasn't committed yet).
  useEffect(() => {
    if (!accountId || !workspaceId || !workspaceName) {
      setAgents([]);
      return;
    }

    let cancelled = false;
    const fetchAgents = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getAgents(accountId, workspaceId, workspaceName);
        if (!cancelled) setAgents(data);
      } catch (err) {
        if (!cancelled) setError('No agents');
        console.error(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchAgents();
    return () => { cancelled = true; };
  }, [accountId, workspaceId, workspaceName]);

  // Auto-select the first agent whenever the current selection isn't in
  // the (freshly-fetched) list. This catches two cases:
  //  1. Initial load — nothing selected, agents just arrived.
  //  2. Workspace change — the old selection is stale and doesn't exist
  //     in the new workspace, so pick the first available agent.
  // Watching [agents, selectedSlug] means we react correctly even when
  // the parent resets selectedSlug asynchronously after workspace change.
  useEffect(() => {
    if (agents.length === 0) return;
    const currentInList = selectedSlug && agents.some(a => a.slug === selectedSlug);
    if (!currentInList) {
      onSelect(agents[0].slug, agents[0].name, agents[0].has_profile ?? false);
    }
    // onSelect intentionally omitted — the parent recreates it on every
    // render and including it would cause an infinite auto-select loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agents, selectedSlug]);

  if (!accountId || !workspaceId || !workspaceName) return null;
  if (loading) return <span style={{ color: '#9ca3af', fontSize: '0.875rem' }}>Loading...</span>;
  if (error) return <span style={{ color: '#6b7280', fontSize: '0.75rem', marginLeft: '1rem' }}>{error}</span>;
  if (agents.length === 0) return <span style={{ color: '#6b7280', fontSize: '0.75rem', marginLeft: '1rem' }}>No agents</span>;

  return (
    <div className="app-header-selector">
      <label>Agent</label>
      <select
        value={selectedSlug || ''}
        onChange={(e) => {
          const agent = agents.find(a => a.slug === e.target.value);
          if (agent) onSelect(agent.slug, agent.name, agent.has_profile ?? false);
        }}
      >
        {agents.map((agent) => (
          <option key={agent.slug} value={agent.slug}>{agent.name}</option>
        ))}
      </select>
    </div>
  );
};

export default AgentSelector;
